import React, { useEffect, useState } from "react";
import { Gift, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import Panda from "./Panda";
import api, { getErrorMessage } from "../lib/api";
import { giftableStickers } from "../data/stickers";
import { useProgress } from "../context/ProgressContext";

// Parent-controlled sticker gifting between siblings: one gift per giver per calendar month.
const GiftStickerDialog = ({ giver, siblings, open, onOpenChange }) => {
  const { refreshChildren } = useProgress();
  const [to, setTo] = useState(siblings[0]?.id || "");
  const [sticker, setSticker] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(siblings[0]?.id || ""); setSticker(""); setStatus(null);
    api.get(`/children/${giver.id}/gift-status`).then((r) => setStatus(r.data)).catch(() => setStatus({ gift_available: true }));
  }, [open, giver.id, siblings]);

  const receiver = siblings.find((s) => s.id === to);
  const options = receiver ? giftableStickers(giver, receiver) : [];

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/children/${giver.id}/gift-sticker`, { to_child_id: to, sticker_id: sticker });
      await refreshChildren();
      toast.success(`${r.data.from_name} gifted a sticker to ${r.data.to_name}!`, { description: `${r.data.to_name}'s sticker book just got a "Gift from ${r.data.from_name}" sticker. Next gift unlocks next month.` });
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not gift the sticker"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="gift-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0c4a6e] flex items-center gap-2" style={{ fontFamily: "Literata, serif" }}><Gift className="text-[#7d5fb2]" /> {giver.panda.name} gifts a sticker</DialogTitle>
          <DialogDescription>One gift a month. {giver.panda.name} keeps the sticker too - it's a gift, not a trade.</DialogDescription>
        </DialogHeader>
        {status && !status.gift_available ? (
          <p className="rounded-2xl bg-[#fff8e1] border border-[#f3c932] p-4 text-sm text-[#5a4600]" data-testid="gift-used">{giver.panda.name} already gifted a sticker this month. The next gift unlocks next month.</p>
        ) : (
          <>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">To</p>
              <div className="flex flex-wrap gap-2">
                {siblings.map((s) => (
                  <button key={s.id} type="button" onClick={() => { setTo(s.id); setSticker(""); }} className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border-2 text-sm font-bold ${to === s.id ? "border-[#7d5fb2] bg-[#f3e8ff] text-[#7d5fb2]" : "border-[#c9ecf5] text-[#0c4a6e]"}`} data-testid={`gift-to-${s.id}`}>
                    <Panda size={28} accessory={s.panda.accessory} scarf={s.panda.scarf} /> {s.panda.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Sticker</p>
              {options.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="gift-none">{receiver ? `${receiver.panda.name} already has every sticker ${giver.panda.name} has unlocked.` : "Pick a sibling first."}</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="gift-options">
                  {options.map((s) => (
                    <button key={s.id} type="button" onClick={() => setSticker(s.id)} className={`relative rounded-2xl p-1.5 border-2 text-left ${sticker === s.id ? "border-[#7d5fb2] bg-[#f3e8ff]" : "border-transparent hover:border-[#c9ecf5]"}`} data-testid={`gift-sticker-${s.id}`}>
                      <img src={`/stickers/${s.file}`} alt={s.name} className="w-full aspect-square object-cover rounded-xl" />
                      <p className="text-[10px] font-bold text-[#0c4a6e] mt-1 leading-tight truncate">{s.name}</p>
                      {sticker === s.id && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#7d5fb2] text-white flex items-center justify-center"><Check size={12} /></span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={send} disabled={busy || !sticker || !to} className="btn-pill bg-[#7d5fb2] hover:bg-[#6a4fa0] text-white w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="gift-send"><Gift size={16} /> {busy ? "Sending..." : "Send the gift"}</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GiftStickerDialog;
